<template>
  <div class="controls">
    <div class="controls__start experiment-header">
      <div class="d-flex justify-content-between align-items-center">
        <span id="experiment-header-title" class="controls__count">{{ currentExperiment ? currentExperiment.name : '' }}</span>
        <EncodedIdField
          :id="currentExperiment?.id"
          :type="'experiment'"
          :hideLabel="true"
          class="ml-2"
        />
      </div>
      <span v-if="isLoading && training_sessions && training_sessions.length > 0" class="controls__count">Training Sessions</span>
      <span v-else class="controls__count">{{ `Training Sessions (${totalTrainingSessions ?? training_sessions.length})` }}</span>
    </div>
    <div class="controls__middle">
      <HelpTip class="ml-2">
        Training session list displays both ongoing and previous sessions associated with a specific experiment.
        You can view details, training metrics (charts), logs, and models by selecting any session card.
        To compare all sessions within the experiment, click on "ALL CHARTS".
      </HelpTip>

      <HelpTip v-if="permissions && !permissions.trainer_write" class="ml-2" help_style="alert">
        This is a read only experiment. Editing functionalities are disabled.
      </HelpTip>
    </div>

    <div class="controls__end">
      <div class="trainer-session-filter-actions">
        <PageControl
          v-if="totalPages > 1"
          v-model="currentPage"
          :totalPages="totalPages"
        />
        <FilterButton
          :filter="taskFilters"
          :filter-count="activeFilterCount"
          :active="showFilterPanel"
          @click="showFilterPanel = !showFilterPanel"
          @clear-filters="clearTaskFilters"
        >
          <v-icon start>mdi-filter</v-icon>
          Filter
        </FilterButton>
        <button
          v-if="!isPublic && currentUsername"
          type="button"
          class="button-light"
          v-tooltip:bottom="isMySessionOnly ? 'Show Sessions for All Users' : `Show Sessions for ${currentUsername} only`"
          @click="filterToMySessions"
        >
          {{ isMySessionOnly ? 'All Users' : 'My Sessions Only' }}
        </button>
      </div>
      <div v-if="!isPublic" class="controls__actions">
        <!-- <button
          v-if="permissions && (permissions.trainer_read || permissions.trainer_write)"
          id="all-charts-btn"
          class="button"
          @click="handleShowAllChartsClick()"
        >
          <SVGIcon
            :iconName="'analytics'"
            :width="'18px'"
            :height="'18px'"
          />All Charts
        </button> -->
        <div v-if="permissions && permissions.trainer_write">
          <ActionsButton
            label="Actions"
            :actions="trainerActionItems"
            buttonStyle="chevron"
            :showCount="true"
            :selectedCount="selectedCount"
            @action-clicked="handleTrainerActionClick"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-if="isLoading" class="empty-state loading-state">
    <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </div>
  <div v-if="!isLoading && filteredTrainingSessions.length === 0" class="empty-state">
    <EmptyState
      :title="'No training sessions found for this experiment'"
      :subtitle="'To start a training session, choose an app from New Trainer Session'"
    />
  </div>
  
  <template v-if="filteredTrainingSessions.length > 0">

    <div class="trainers-card-container scrollbar">
      <TrainerCard
        v-for="session in filteredTrainingSessions"
        :key="session.id"
        :session="session"
        :currentProject="currentProject"
        :permissions="permissions"
        :trainerApps="trainerApps"
        @info-clicked="(session) => handleInfoClicked(session)"
        @clone-training-session="handleCloneTrainingSession"
        @card-action="handleCardAction"
        @delete-session="openConfirmDeleteSessionModal"
        @terminate-session="openConfirmStopTrainingSessionModal"
        @delete-trainer="openConfirmDeleteTrainerModal(t)"
        @charts-clicked="(session) =>handleSessionChartsClicked(session)"
        @refresh-session="(session) => handleRefreshSession(session)"
        @resume-training-session="(session) => openConfirmResumeSessionModal(session)"
        @session-selected="handleSessionSelected"
        @edit-trainer="handleEditTrainerClick"
        @description-changed="handleSessionDescriptionChanged"
      />
    </div>
  </template>

  <div
    ref="filterPanelEl"
    :class="['trainer-filter-panel', { 'trainer-filter-panel--open': showFilterPanel }]"
  >
    <div class="trainer-filter-panel__header">
      <h3>Filters</h3>
      <div class="trainer-filter-panel__header-actions">
        <button
          type="button"
          class="trainer-filter-panel__clear"
          :disabled="activeFilterCount === 0"
          @click="clearTaskFilters"
        >
          Clear All
        </button>
        <button class="trainer-filter-panel__close" @click="showFilterPanel = false">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
    </div>
    <div class="trainer-filter-panel__content">
      <TasksFilter
        :model-value="taskFilters"
        @update:modelValue="handleTaskFiltersChanged"
        :status-list="statusList"
        :users-list="usersList"
        :types-list="typesList"
        :manage-type-list="manageTypeList"
      />
    </div>
  </div>

  <TrainingSessionDetailsModal
    ref="trainingSessionDetailsModal"
    :session="selectedTrainingSession"
    :permissions="permissions"
    @stop-training="openConfirmStopTrainingSessionModal"
    @delete-session="openConfirmDeleteSessionModal"
  />
  <TrainingSessionAnalyticsModal
    ref="trainingSessionAnalyticsModal"
    :trainerSession="trainerToShowAnalytics"
    :projectID="currentProject ? currentProject.id : 0"
  />
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <SessionMoveDialog
    ref="sessionMoveDialog"
    :selectedSessions="selectedSessionsObjects"
    :currentTrainerId="currentExperiment?.id"
    @move-sessions="RefreshList"
  />
  <SessionDeleteDialog
    ref="sessionDeleteDialog"
    :selectedSessions="selectedSessionsObjects"
    @delete-sessions="handleDeleteSessionsConfirmed"
  />
  <SessionEditDialog
    ref="sessionEditDialog"
    :selectedSessions="selectedSessionsObjects"
    @edit-session="handleEditSessionConfirmed"
  />
  <AppStartModal
    v-if="!isPublic && selectedTrainerAppName"
    ref="trainerAppModal"
    :appName="selectedTrainerAppName"
    :defaultValues="trainerStartDefaultValues"
    :metaData="trainerStartMetaData"
    :initialName="cloneSessionName"
    :defaultSchemaValues="cloneDefaultSchemaValues"
    @job_started="handleTrainerAppJobStarted"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import TrainerCard from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainerSessionCard3.vue';
import TrainingSessionDetailsModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingSessionDetailsModalV2.vue';
import TrainingSessionAnalyticsModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingSessionAnalyticsModal.vue';
import SessionMoveDialog from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionMoveDialog.vue';
import SessionDeleteDialog from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionDeleteDialog.vue';
import SessionEditDialog from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionEditDialog.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import ActionsButton from '@/components/ActionsButton.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import EmptyState from '@/components/EmptyState.vue';
import HelpTip from '../../HelpTip.vue';
import { decodeID, encodeID } from '@/assets/js/utils.js';
import { goto_training_charts, goto_trainer_session } from '@/assets/js/links.js';
import AppStartModal from '@/components/Apps/AppStartModal.vue';
import TasksFilter from '@/components/Apps/TasksFilter.vue';
import PageControl from '@/components/DatasetComponent/GalleryComponent/PageControl.vue';
import FilterButton from '@/components/FilterButton.vue';

const $toast = useToast();
const TRAINER_TASK_INFO_SOCKET_KEY = '__trainerTaskInfoSocket';

export default {
  name: "TrainerSessionManagement",
  components: {
    TrainerCard,
    TrainingSessionDetailsModal,
    TrainingSessionAnalyticsModal,
    ConfirmModal,
    EncodedIdField,
    SVGIcon,
    ActionsButton,
    HelpTip,
    EmptyState,
    SessionMoveDialog,
    SessionDeleteDialog,
    SessionEditDialog,
    AppStartModal,
    TasksFilter,
    PageControl,
    FilterButton,
  },
  inject: {
    experiments: { default: null },
    isPublic: { default: false },
  },
  props: {
    projectId: [String, Number],
    expId: [String, Number],
  },
  emits: [
    'trainer-set-created', 'update-list',
  ],
  data() {
    return {
      dataConnect: null,
      isLoading: true,
      training_sessions: [],
      trainingSessionList: [],
      trainerToShowAnalytics: null,
      loadingFinished: true,
      selectedTrainingSession: null,
      selectedTrainingExperiment: null,
      selectedTrainerDetails: null,
      confirmMessage: null,
      confirmMessageHeader: null,
      toDelete: null,
      confirmFunction: null,
      buttonText: 'Delete',
      modalTrainer: null,
      permissions: null,
      openedDropdowns: [],
      trainerApps: [],
      isLoadingTrainerApps: false,
      selectedTrainerAppName: '',
      cloneSessionName: '',
      cloneDefaultSchemaValues: {},
      selectedSessions: {},
      currentExperiment: null,
      trainerActions: [
        {
          icon: 'folder2',
          label: 'Move',
          eventKey: 'move',
          tooltip: 'Move selected training sessions to another experiment',
        },
        {
          icon: 'chart',
          label: 'Profile',
          eventKey: 'profile',
          tooltip: 'View training session profile',
        },
        {
          icon: 'delete',
          label: 'Delete',
          eventKey: 'delete',
          isAlert: true,
          tooltip: 'Delete selected training sessions',
        },
      ],
      taskInfoSocket: null,
      taskInfoReconnectTimeout: null,
      isIntentionalTaskInfoSocketClose: false,
      trainerFilterDefinitions: [],
      currentPage: 1,
      pageSize: 10,
      totalTrainingSessions: null,
      hasMoreTrainingSessions: false,
      showFilterPanel: false,
      taskFilters: {
        startDate: '',
        endDate: '',
        showDeleted: false,
        dynamicFilters: {},
      },
    };
  },
  computed: {
    currentUsername() {
      return this.$store.state.user?.user?.username || '';
    },
    isMySessionOnly() {
      const usernames = this.taskFilters.dynamicFilters?.username || [];
      return usernames.length === 1 && usernames[0] === this.currentUsername;
    },
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    trainerStartDefaultValues() {
      return {
        trainer: {
          project_id: this.currentProject?.id || this.currentExperiment?.project_id || null,
          project_name: this.currentProject?.name || '',
          experiment_id: this.currentExperiment?.id || null,
          experiment_name: this.currentExperiment?.name || '',
        },
      };
    },
    trainerStartMetaData() {
      return this.currentExperiment?.id
        ? { experiment_id: this.currentExperiment.id }
        : {};
    },
    selectedSessionsObjects() {
      return this.training_sessions.filter(
        (session) => this.selectedSessions[session.id]
      );
    },
    statusList() {
      return [...new Set(this.training_sessions.map((session) => session?.docker_task?.status || session.status).filter(Boolean))];
    },
    usersList() {
      return this.getFilterOptions('username');
    },
    typesList() {
      return this.getFilterOptions('model_type');
    },
    manageTypeList() {
      return [...new Set(this.training_sessions.map((session) => session?.docker_task?.manage_type).filter(Boolean))];
    },
    filteredTrainingSessions() {
      return this.training_sessions;
    },
    totalPages() {
      if (Number.isFinite(this.totalTrainingSessions)) {
        return Math.max(1, Math.ceil(this.totalTrainingSessions / this.pageSize));
      }
      return Math.max(1, this.currentPage + Number(this.hasMoreTrainingSessions));
    },
    activeFilterCount() {
      const dynamic = this.taskFilters.dynamicFilters || {};
      return Number(Boolean(this.taskFilters.startDate))
        + Number(Boolean(this.taskFilters.endDate))
        + Number(Boolean(this.taskFilters.showDeleted))
        + ['name', 'status', 'types', 'manage_types', 'username'].reduce((count, key) => {
          const value = dynamic[key];
          return count + Number((Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value.trim().length > 0));
        }, 0);
    },
    selectedCount() {
      return Object.keys(this.selectedSessions).length;
    },
    trainerActionItems() {
      const trainerAppActions = this.trainerApps.length > 0
        ? this.trainerApps.map((app) => ({
          icon: 'model',
          label: app.title || app.name,
          eventKey: `trainer-app:${app.name}`,
          tooltip: app.description || `Start ${app.title || app.name}`,
        }))
        : [{
          label: this.isLoadingTrainerApps ? 'Loading trainer apps...' : 'No trainer apps available',
          eventKey: 'trainer-apps-empty',
          disabled: true,
        }];

      return [
        {
          icon: 'add',
          label: 'New Trainer Session',
          eventKey: 'new-trainer-session',
          tooltip: 'Choose a trainer app to start a session',
          children: trainerAppActions,
        },
        ...this.trainerActions,
      ];
    },
    currentSession: {
      get() {
        return this.$store.state.experiments.currentSession;
      },
      set(session) {
        this.$store.commit('experiments/setCurrentSession', session);
      },
    },
  },
  watch: {
    currentPage() {
      if (this.currentExperiment?.id) {
        this.getTrainingSessions(this.currentExperiment.id);
      }
    },
  },
  async created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  beforeUnmount() {
    this.disconnectTaskInfoSocket();
    document.removeEventListener('mousedown', this.handleFilterPanelOutsideClick);
  },
  async mounted() {
    document.addEventListener('mousedown', this.handleFilterPanelOutsideClick);
    this.loadTrainerApps();

    // Get expId from route params and decode it if needed
    const expIdParam = this.$route.params.expId;
    const decimalExpId = expIdParam ? decodeID(expIdParam) || parseInt(expIdParam, 10) : null;
    
    if (decimalExpId) {
      await this.handleGetExperiment(decimalExpId);
    }

    this.getProjectAccessForSingleUser();
  },
  methods: {
    handleFilterPanelOutsideClick(event) {
      if (!this.showFilterPanel) return;

      const target = event.target;
      const panel = this.$refs.filterPanelEl;
      if (panel?.contains(target)) return;
      if (target.closest?.('.filter-button-host, .popper, .popper-content, .v-overlay-container, .v-menu')) return;

      this.showFilterPanel = false;
    },
    clearTaskFilters() {
      this.taskFilters = {
        startDate: '',
        endDate: '',
        showDeleted: false,
        dynamicFilters: {},
      };
      this.currentPage = 1;
      if (this.currentExperiment?.id) {
        this.getTrainingSessions(this.currentExperiment.id);
      }
    },
    handleTaskFiltersChanged(filters) {
      this.taskFilters = filters;
      if (!this.currentExperiment?.id) return;

      if (this.currentPage !== 1) {
        this.currentPage = 1;
        return;
      }

      this.getTrainingSessions(this.currentExperiment.id);
    },
    filterToMySessions() {
      const username = this.isMySessionOnly ? [] : [this.currentUsername];
      this.handleTaskFiltersChanged({
        ...this.taskFilters,
        dynamicFilters: {
          ...(this.taskFilters.dynamicFilters || {}),
          username,
        },
      });
    },
    getFilterOptions(name) {
      const filter = this.trainerFilterDefinitions.find((item) => item.name === name);
      return filter?.options || [];
    },
    getFilterName(matchers, fallback) {
      const filter = this.trainerFilterDefinitions.find((item) => {
        const name = String(item.name || '').toLowerCase();
        const label = String(item.label || '').toLowerCase();
        return matchers.some((matcher) => name === matcher || label === matcher);
      });
      return String(filter?.name || fallback).toLowerCase();
    },
    getTaskInfoWebSocketEndpoint() {
      return '/ws/task_info';
    },
    getTaskInfoWebSocketUrl() {
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // Same-origin socket allows auth cookie to be sent in the handshake.
      return `${protocol}://${window.location.host}${this.getTaskInfoWebSocketEndpoint()}`;
    },
    getSubscribedTaskIds() {
      return (this.training_sessions || [])
        .map((session) => Number(session?.docker_task?.id))
        .filter((taskId) => Number.isInteger(taskId) && taskId > 0);
    },
    clearTaskInfoReconnectTimeout() {
      if (this.taskInfoReconnectTimeout) {
        clearTimeout(this.taskInfoReconnectTimeout);
        this.taskInfoReconnectTimeout = null;
      }
    },
    scheduleTaskInfoReconnect() {
      this.clearTaskInfoReconnectTimeout();
      this.taskInfoReconnectTimeout = setTimeout(() => {
        this.connectTaskInfoSocket();
      }, 10000);
    },
    extractTaskInfoUpdates(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (payload && typeof payload === 'object' && Number.isInteger(Number(payload.id))) {
        return [payload];
      }

      if (Array.isArray(payload?.tasks)) {
        return payload.tasks;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      if (payload?.task && typeof payload.task === 'object') {
        return [payload.task];
      }

      return [];
    },
    applyTaskInfoUpdates(updates) {
      if (!Array.isArray(updates) || updates.length === 0) {
        return;
      }

      const updatesByTaskId = new Map(
        updates
          .map((update) => [Number(update?.id), update])
          .filter(([taskId]) => Number.isInteger(taskId) && taskId > 0)
      );

      if (updatesByTaskId.size === 0) {
        return;
      }

      this.training_sessions = this.training_sessions.map((session) => {
        const dockerTask = session?.docker_task || {};
        const taskId = Number(dockerTask?.id);
        const update = updatesByTaskId.get(taskId);

        if (!update) {
          return session;
        }

        const statusUpdateRaw = update?.status_update ?? update?.statis_update ?? update?.statusUpdate;
        const statusUpdate = statusUpdateRaw && typeof statusUpdateRaw === 'object'
          ? statusUpdateRaw
          : null;

        const mergedStatusInfo = statusUpdate ? {
          ...(dockerTask.status_info || {}),
          status: statusUpdate.status ?? dockerTask.status_info?.status,
          message: statusUpdate.message ?? dockerTask.status_info?.message,
          label: statusUpdate.label ?? dockerTask.status_info?.label,
          stage_name: statusUpdate.stage ?? dockerTask.status_info?.stage_name,
          stage_label: statusUpdate.label ?? dockerTask.status_info?.stage_label,
          percent: statusUpdate.percentage ?? dockerTask.status_info?.percent,
          stage_number: statusUpdate.stage_number ?? dockerTask.status_info?.stage_number,
          total_stages: statusUpdate.total_stages ?? dockerTask.status_info?.total_stages,
        } : dockerTask.status_info;

        return {
          ...session,
          appname: update.appname ?? session.appname,
          docker_task: {
            ...dockerTask,
            appname: update.appname ?? dockerTask.appname,
            name: dockerTask.name,
            status: update.status ?? update.name ?? dockerTask.status,
            message: statusUpdate?.message ?? update.message ?? dockerTask.message,
            formatted_duration: update.formatted_duration ?? dockerTask.formatted_duration,
            status_info: mergedStatusInfo,
            status_process_info: update.status_process_info ?? dockerTask.status_process_info,
            cloud_instance_id: dockerTask.cloud_instance_id,
            common_app_params: update.common_app_params ?? dockerTask.common_app_params,
            app_info: update.app_info ?? dockerTask.app_info,
          },
        };
      });

      if (this.selectedTrainingSession?.docker_task?.id) {
        const selectedTaskId = Number(this.selectedTrainingSession.docker_task.id);
        const selectedUpdate = updatesByTaskId.get(selectedTaskId);
        if (selectedUpdate) {
          const statusUpdateRaw = selectedUpdate?.status_update ?? selectedUpdate?.statis_update ?? selectedUpdate?.statusUpdate;
          const statusUpdate = statusUpdateRaw && typeof statusUpdateRaw === 'object'
            ? statusUpdateRaw
            : null;
          const selectedTask = this.selectedTrainingSession.docker_task || {};

          this.selectedTrainingSession = {
            ...this.selectedTrainingSession,
            appname: selectedUpdate.appname ?? this.selectedTrainingSession.appname,
            docker_task: {
              ...selectedTask,
              appname: selectedUpdate.appname ?? selectedTask.appname,
              name: selectedTask.name,
              status: selectedUpdate.status ?? selectedUpdate.name ?? selectedTask.status,
              message: statusUpdate?.message ?? selectedUpdate.message ?? selectedTask.message,
              formatted_duration: selectedUpdate.formatted_duration ?? selectedTask.formatted_duration,
              status_info: statusUpdate ? {
                ...(selectedTask.status_info || {}),
                status: statusUpdate.status ?? selectedTask.status_info?.status,
                message: statusUpdate.message ?? selectedTask.status_info?.message,
                label: statusUpdate.label ?? selectedTask.status_info?.label,
                stage_name: statusUpdate.stage ?? selectedTask.status_info?.stage_name,
                stage_label: statusUpdate.label ?? selectedTask.status_info?.stage_label,
                percent: statusUpdate.percentage ?? selectedTask.status_info?.percent,
                stage_number: statusUpdate.stage_number ?? selectedTask.status_info?.stage_number,
                total_stages: statusUpdate.total_stages ?? selectedTask.status_info?.total_stages,
              } : selectedTask.status_info,
              status_process_info: selectedUpdate.status_process_info ?? selectedTask.status_process_info,
              cloud_instance_id: selectedTask.cloud_instance_id,
              common_app_params: selectedUpdate.common_app_params ?? selectedTask.common_app_params,
              app_info: selectedUpdate.app_info ?? selectedTask.app_info,
            },
          };
        }
      }
    },
    sendTaskInfoSubscription(taskIds = this.getSubscribedTaskIds()) {
      if (!this.taskInfoSocket || this.taskInfoSocket.readyState !== WebSocket.OPEN) {
        return;
      }

      this.taskInfoSocket.send(JSON.stringify({ task_ids: taskIds }));
    },
    disconnectTaskInfoSocket() {
      this.clearTaskInfoReconnectTimeout();
      this.isIntentionalTaskInfoSocketClose = true;

      if (this.taskInfoSocket) {
        if (
          this.taskInfoSocket.readyState === WebSocket.OPEN
          || this.taskInfoSocket.readyState === WebSocket.CONNECTING
        ) {
          this.taskInfoSocket.close();
        }
        this.taskInfoSocket = null;
      }

      if (window[TRAINER_TASK_INFO_SOCKET_KEY]) {
        window[TRAINER_TASK_INFO_SOCKET_KEY] = null;
      }
    },
    connectTaskInfoSocket() {
      if (this.taskInfoSocket) {
        if (this.taskInfoSocket.readyState === WebSocket.OPEN) {
          this.sendTaskInfoSubscription();
          return;
        }
        if (
          this.taskInfoSocket.readyState === WebSocket.CONNECTING
          || this.taskInfoSocket.readyState === WebSocket.CLOSING
        ) {
          return;
        }
      }

      const existingSocket = window[TRAINER_TASK_INFO_SOCKET_KEY];
      if (
        existingSocket
        && existingSocket !== this.taskInfoSocket
        && (
          existingSocket.readyState === WebSocket.OPEN
          || existingSocket.readyState === WebSocket.CONNECTING
        )
      ) {
        // Ensure only one trainer task_info socket is active.
        existingSocket.close();
      }

      this.clearTaskInfoReconnectTimeout();
      this.isIntentionalTaskInfoSocketClose = false;

      const socket = new WebSocket(this.getTaskInfoWebSocketUrl());
      this.taskInfoSocket = socket;
      window[TRAINER_TASK_INFO_SOCKET_KEY] = socket;

      socket.onopen = () => {
        this.sendTaskInfoSubscription();
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);

          if (payload?.message_type === 'error' || payload?.error) {
            console.error('Task info stream error:', payload?.message || payload?.error);
            return;
          }

          const updates = this.extractTaskInfoUpdates(payload);
          this.applyTaskInfoUpdates(updates);
        } catch (err) {
          console.error('Failed to parse task info frame:', err);
        }
      };

      socket.onclose = () => {
        this.taskInfoSocket = null;
        if (window[TRAINER_TASK_INFO_SOCKET_KEY] === socket) {
          window[TRAINER_TASK_INFO_SOCKET_KEY] = null;
        }
        if (!this.isIntentionalTaskInfoSocketClose && this.getSubscribedTaskIds().length > 0) {
          this.scheduleTaskInfoReconnect();
        }
      };

      socket.onerror = () => {
        // Reconnect is handled by onclose.
      };
    },
    handleTrainerAppJobStarted() {
      if (this.currentExperiment?.id) {
        this.getTrainingSessions(this.currentExperiment.id, false);
      }
    },
    async getTrainingSessions(id, showLoading = true) {
      if (showLoading) this.isLoading = true;

      try {
        this.training_sessions = await this.getTrainingSessionList(id);

        const subscribedTaskIds = this.getSubscribedTaskIds();
        if (subscribedTaskIds.length === 0) {
          this.disconnectTaskInfoSocket();
        } else {
          this.connectTaskInfoSocket();
          this.sendTaskInfoSubscription(subscribedTaskIds);
        }
      } finally {
        if (showLoading) this.isLoading = false;
      }
    },
    async getProjectAccessForSingleUser() {
      if (this.currentProject && this.$store.state.user?.user?.username) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentProject.id,
          username: this.$store.state.user.user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            } else if (data.error) {
              $toast.open({
                queue: true,
                dismissible: true,
                message: `Failed to retrieve project access: ${data.error.message}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve project access: ${e}`,
              type: "error",
              position: "top",
            });
          });
      }
    },

    openConfirmStopTrainingSessionModal(session) {
      this.confirmMessage = `Are you sure you want to stop training session "${session.name}"?\n`;
      this.confirmMessageHeader = 'Stop Training Session';
      this.toDelete = session;
      this.buttonText = 'Stop';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleStopTrainingSessions([session.id]);
      };
    },
    openConfirmDeleteSessionModal(session) {
      this.confirmMessage = `Are you sure you want to move training session "${session.name}" to recycle bin? This will also move training artifacts and validate sessions to recycle bin.`;
      this.confirmMessageHeader = 'Remove Training Session';
      this.toDelete = session;
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleDeleteSession(session.id);
      };
    },
     openConfirmResumeSessionModal(session) {
      this.confirmMessage = `Are you sure you want to resume training session "${session.name}"?`;
      this.confirmMessageHeader = 'Resume Training Session';
      this.toDelete = session;
      this.buttonText = 'Resume';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleResumeSession(session.id);
      };
    },
    handlePauseClicked() {
    },
    async handleRefreshSession(session) {
      const index = this.training_sessions.findIndex((e) => e.id === session.id);
      if (index > -1) {
        this.training_sessions[index] = await this.getSession(session.id);
      }
    },
    async updateEC2InstanceStatus() {
      await this.dataConnect.updateEC2Status({
        trainer_ids: this.trainerIds,
      })
        .catch((e) => console.log(e));
    },
    async handleTrainerCreated() {
      this.getTrainingSessions(this.currentExperiment.id);
    },
    async getTrainingSessionList(experiment_id) {
      const dynamicFilters = this.taskFilters?.dynamicFilters || {};
      const filters = {};

      if (this.taskFilters.startDate) filters.start_date = this.taskFilters.startDate;
      if (this.taskFilters.endDate) filters.end_date = this.taskFilters.endDate;
      if (this.taskFilters.showDeleted) filters.deleted = true;

      const name = String(dynamicFilters.name || '').trim();
      if (name) filters.name = name;

      if (dynamicFilters.status?.length) {
        filters[this.getFilterName(['status', 'statuses'], 'status')] = dynamicFilters.status;
      }

      if (dynamicFilters.types?.length) {
        filters[this.getFilterName(['type', 'types', 'model_type'], 'model_type')] = dynamicFilters.types;
      }

      if (dynamicFilters.manage_types?.length) {
        filters.manage_types = dynamicFilters.manage_types;
      }

      if (dynamicFilters.username?.length) {
        filters[this.getFilterName(['user', 'users', 'username'], 'username')] = dynamicFilters.username;
      }

      const resp = await this.dataConnect.getTrainingSessionsWithFilter({
        experiment_id,
        offset: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        reverse: true,
        filters,
      })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training sessions: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return [];
      }

      const responseData = resp.result;
      const sessions = Array.isArray(responseData)
        ? responseData
        : (responseData?.train_session || []);

      const totalCount = responseData?.session_count
        ?? responseData?.train_session_count
        ?? responseData?.training_session_count
        ?? responseData?.total_count
        ?? responseData?.total
        ?? responseData?.count;
      this.totalTrainingSessions = Number.isFinite(Number(totalCount)) ? Number(totalCount) : null;
      this.hasMoreTrainingSessions = sessions.length === this.pageSize;

      if (!Array.isArray(responseData)) {
        this.trainerFilterDefinitions = responseData?.filters || [];
      }

      if (sessions.length > 0) {
        sessions.forEach((session) => {
          if (this.selectedTrainingSession && session.id === this.selectedTrainingSession.id) {
            this.selectedTrainingSession = session;
          }
        });
      }
      return sessions;
    },
    async getSession(id) {
      const resp = await this.dataConnect.getTrainingSession({
        trainer_session_id: id,
      })
        .catch((e) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training session: ${e}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training session: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return null;
      }
      return resp.result;
    },
    // async handleSessionAnalyticsClick() {
    //   this.trainerToShowAnalytics = { trainer_id: expID };
    //   await this.$nextTick();
    //   this.$refs.trainingSessionAnalyticsModal.showModal();
    // },
    async launchTrainedModelCompare(trainer_ids) {
      this.trainerToShowAnalytics = { trainer_ids };
      await this.$nextTick();
      this.$refs.trainingSessionAnalyticsModal.showModal();
    },
    async handleStopTrainingSessions(ids) {
      await this.dataConnect.terminateTrainingSessions({
        session_ids: ids,
      })
        .then((data) => {
          this.updateEC2InstanceStatus();
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to stop training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to stop training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleResumeSession(id) {
      await this.dataConnect.resumeTrainingSession({
        session_id: id,
      })
        .then(async (data) => {
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to resume training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          } else if (data.result) {
            this.getTrainingSessions(this.currentExperiment.id);
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to delete training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleGetExperiment(id) {
      await this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (data.result) {
            this.currentExperiment = data.result;
            this.getTrainingSessions(this.currentExperiment.id);
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleInfoClicked(session) {
      this.currentSession = session;
      goto_trainer_session(this.$route.params.projectId, this.currentExperiment.id, session.id);
    },
    handleCardAction({ action, session }) {
      if (action === 'details') {
        this.handleInfoClicked(session);
      } else if (action === 'charts') {
        this.handleSessionChartsClicked(session);
      } else if (action === 'artifacts') {
        goto_trainer_session(this.$route.params.projectId, this.currentExperiment.id, session.id, { mode: 'models' });
      } else if (action === 'delete') {
        this.openConfirmDeleteSessionModal(session);
      } else if (action === 'resume') {
        this.openConfirmResumeSessionModal(session);
      } else if (action === 'edit') {
        this.$refs.sessionEditDialog.openModal(session);
      } else if (action === 'move') {
        this.selectedSessions = { [session.id]: true };
        this.$nextTick(() => this.$refs.sessionMoveDialog.openModal());
      }
    },
    handleEditTrainerClick(session) {
      this.$refs.sessionEditDialog.openModal(session);
    },
    handleSessionChartsClicked(session) {
      this.currentSession = session;
      goto_trainer_session(this.$route.params.projectId, this.currentExperiment.id, session.id, { mode: 'charts' });
    },
    handleShowAllChartsClick() {
      goto_training_charts(this.$route.params.projectId, this.currentExperiment.id);
    },
    handleSessionSelected(payload) {
      if (payload.isSelected) {
        this.selectedSessions[payload.sessionId] = true;
      } else {
        delete this.selectedSessions[payload.sessionId];
      }
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    getSelectedSessionIds() {
      return Object.keys(this.selectedSessions);
    },
    handleDeleteSelectedSessions() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select at least one session to delete',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      this.showActionsDropdown = false;
      this.$refs.sessionDeleteDialog.openModal();
    },
    handleMoveSelectedSessions() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select at least one session to move',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      // Open move dialog with selected sessions
      this.$refs.sessionMoveDialog.openModal();
    },
    handleEditSelectedSessions() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select a session to edit',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      if (selectedIds.length > 1) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'You can only edit one session at a time',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      const selectedSession = this.selectedSessionsObjects[0];
      this.showActionsDropdown = false;
      this.$refs.sessionEditDialog.openModal(selectedSession);
    },
    handleProfileSelectedSession() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select a session to view profile',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      if (selectedIds.length > 1) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'You can only view profile for one session at a time',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      this.$router.push({ path: '/profile' });
    },
    handleTrainerActionClick(eventKey) {
      if (eventKey.startsWith('trainer-app:')) {
        this.cloneSessionName = '';
        this.cloneDefaultSchemaValues = {};
        this.selectedTrainerAppName = eventKey.slice('trainer-app:'.length);
        this.$nextTick(() => this.$refs.trainerAppModal?.showModal());
        return;
      }

      switch (eventKey) {
        case 'move':
          this.handleMoveSelectedSessions();
          break;
        case 'edit':
          this.handleEditSelectedSessions();
          break;
        case 'profile':
          this.handleProfileSelectedSession();
          break;
        case 'delete':
          this.handleDeleteSelectedSessions();
          break;
        default:
          break;
      }
    },
    async handleCloneTrainingSession({ session, app }) {
      if (!session || !app?.name) return;
      const sourceName = session.name || session.docker_task?.name || 'Session';
      let taskParams = session.docker_task?.params;

      if (!taskParams || typeof taskParams !== 'object' || Object.keys(taskParams).length === 0) {
        const taskId = Number(session.docker_task?.id);
        if (Number.isInteger(taskId) && taskId > 0) {
          try {
            const response = await this.dataConnect.api('task.get', { id: taskId });
            if (response?.error) {
              throw new Error(response.error.message || 'Failed to fetch task params');
            }
            const task = response?.result?.task || response?.result;
            taskParams = task?.params;
          } catch (error) {
            console.error('Failed to load trainer task params for cloning:', error);
          }
        }
      }

      this.cloneSessionName = `${sourceName}-COPY`;
      this.cloneDefaultSchemaValues = taskParams || {};
      this.selectedTrainerAppName = app.name;
      this.$nextTick(() => this.$refs.trainerAppModal?.showModal());
    },
    async loadTrainerApps() {
      this.isLoadingTrainerApps = true;
      try {
        const response = await this.dataConnect.api('app.list', { filter: { type: 'trainer' } });
        if (response.error) {
          throw new Error(response.error.message || 'Failed to load trainer apps');
        }
        this.trainerApps = Array.isArray(response.result) ? response.result : [];
      } catch (error) {
        this.trainerApps = [];
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to load trainer apps: ${error.message}`,
          type: 'error',
          position: 'top',
        });
      } finally {
        this.isLoadingTrainerApps = false;
      }
    },
    async handleDeleteMultipleSessions(sessions) {
      try {
        const sessionIds = sessions.map(session => session.id);
        const expId = sessions[0].trainer_id;
        
        await this.dataConnect.deleteTrainingSession({
          session_ids: sessionIds,
          exp_id: expId,
        });
        
        // Remove from selected sessions
        sessions.forEach(session => {
          delete this.selectedSessions[session.id];
        });
        
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Successfully deleted ${sessions.length} session(s)`,
          type: 'success',
          position: 'top',
        });
        
        // Refresh the list
        this.getTrainingSessions(this.currentExperiment.id, false);
      } catch (error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to delete sessions: ${error}`,
          type: 'error',
          position: 'top',
        });
      }
    },
    handleDeleteSessionsConfirmed(payload) {
      if (payload.success) {
        // Clear selected sessions and refresh list
        this.selectedSessions = {};
        this.getTrainingSessions(this.currentExperiment.id, false);
      }
    },
    handleEditSessionConfirmed(editedSession) {
      // Update the session in the local list
      const idx = this.training_sessions.findIndex(s => s.id === editedSession.id);
      if (idx >= 0) {
        this.training_sessions[idx].name = editedSession.name;
        this.training_sessions[idx].description = editedSession.description;
      }
      // Clear selected sessions
      this.selectedSessions = {};
    },
    async handleSessionDescriptionChanged(payload) {
      const session = payload?.session;
      if (!session?.id) {
        return;
      }

      await this.dataConnect.updateTrainingSession({
        id: session.id,
        name: session.docker_task?.name || session.name || '',
        description: payload?.description || '',
      });

      const idx = this.training_sessions.findIndex((s) => s.id === session.id);
      if (idx >= 0) {
        this.training_sessions[idx].description = payload?.description || '';
      }
    },
    async RefreshList(payload) {
      
        this.getTrainingSessions(this.currentExperiment.id, false);
     
    },
  },
};
</script>

<style lang="scss" scoped>

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;

    &.experiment-header {
      flex-direction: column;
    }
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    width: fit-content;
    align-items: center;
    gap: 12px;
  }

  &__middle {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

.trainer-session-filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 4px;
  border-radius: 50%;
  padding: 0 5px;
  background: #e53935;
  color: white;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.trainer-filter-panel {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  width: 0;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  background: var(--color-white-100);
  border-left: 1px solid var(--gray-300);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease-in-out;
}

.trainer-filter-panel--open {
  width: 380px;
}

.trainer-filter-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  width: 380px;
  padding: 16px;
  border-bottom: 1px solid var(--gray-300);
}

.trainer-filter-panel__header h3 {
  margin: 0;
}

.trainer-filter-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.trainer-filter-panel__clear {
  padding: 6px 8px;
  border: 1px solid #555;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.trainer-filter-panel__clear:hover:not(:disabled) {
  background: rgba(85, 85, 85, 0.08);
}

.trainer-filter-panel__clear:disabled {
  opacity: 0.5;
  cursor: default;
}

.trainer-filter-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.trainer-filter-panel__content {
  flex: 1 1 auto;
  width: 380px;
  padding: 16px;
  overflow-y: auto;
}

@media (max-width: 380px) {
  .trainer-filter-panel--open,
  .trainer-filter-panel__header,
  .trainer-filter-panel__content {
    width: 100vw;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}
.trainers-card-container {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 16px;
  overflow-y: scroll;
  gap: 16px;
}

.actions-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 150px;
  margin-top: 4px;
}

.dropdown-item {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:first-child {
    border-radius: 4px 4px 0 0;
  }

  &:last-child {
    border-radius: 0 0 4px 4px;
  }
}

.trainer-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;

  &__message {
    position: absolute;
    display: flex;
    flex-direction: column;
    text-align: center;
    top: 60%;
    @include themify() {
      color: themed('body-text-color');
    }
  }
}

.loading-state {
  position: fixed;
  inset: 0;
  z-index: 1100;
  width: 100vw;
  height: 100vh;
  background: transparent;
  pointer-events: none;
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }

#actions-btn {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
